/**
 * Reimbursable meals claimed, counted per MEAL, never per main row (Amana,
 * 2026-11-03). A meal can carry two main-type items: Amana's breakfast is ONE
 * bundle, "Cereal & String Cheese • Orange Wedges • Milk", whose cereal and
 * string cheese ship as two deliverables of 100. Summing the main rows claimed
 * 200 meals for 100 served. Rows that share a meal key (the meal's bundle, or
 * the menu item the meal was ordered as) are ONE meal, which takes its
 * largest count; different meals still add up. A row with no key is a meal of
 * its own, as before. Pure.
 */

/** One main row: the meals it belongs to, and its three claim counts. */
export interface MealClaimRow {
  mealKeys: readonly string[]
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
}

export interface MealClaimTotal {
  served: number | null
  reimbursable: number | null
  nonReimbursable: number | null
}

/** The section title over a meal's mains when they are eaten together, not chosen between. */
export const SERVED_TOGETHER_TITLE = 'Main items'
/** Its caption under offer vs. serve: the items make up one meal. */
export const SERVED_TOGETHER_CAPTION = 'served together as one meal'

/** The rows grouped into meals: rows sharing any key join one meal, first-seen order. */
export function mealsOf<T extends Pick<MealClaimRow, 'mealKeys'>>(rows: readonly T[]): T[][] {
  const parent = rows.map((_, i) => i)
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!
      i = parent[i]!
    }
    return i
  }
  const owner = new Map<string, number>()
  rows.forEach((row, i) => {
    for (const key of row.mealKeys) {
      const seen = owner.get(key)
      if (seen === undefined) {
        owner.set(key, i)
      } else {
        const a = find(seen)
        const b = find(i)
        if (a !== b) {
          parent[Math.max(a, b)] = Math.min(a, b)
        }
      }
    }
  })
  const groups = new Map<number, T[]>()
  rows.forEach((row, i) => {
    const root = find(i)
    groups.set(root, [...(groups.get(root) ?? []), row])
  })
  return [...groups.values()]
}

function maxNullable(values: ReadonlyArray<number | null>): number | null {
  return values.reduce<number | null>(
    (max, value) => (value === null ? max : max === null ? value : Math.max(max, value)),
    null,
  )
}

function addNullable(a: number | null, b: number | null): number | null {
  return a === null ? b : b === null ? a : a + b
}

/** Σ over meals of each meal's largest Served / Reimb / Non-reimb. */
export function claimedMeals(rows: readonly MealClaimRow[]): MealClaimTotal {
  let total: MealClaimTotal = { served: null, reimbursable: null, nonReimbursable: null }
  for (const meal of mealsOf(rows)) {
    total = {
      served: addNullable(total.served, maxNullable(meal.map((row) => row.served))),
      reimbursable: addNullable(total.reimbursable, maxNullable(meal.map((row) => row.reimbursable))),
      nonReimbursable: addNullable(total.nonReimbursable, maxNullable(meal.map((row) => row.nonReimbursable))),
    }
  }
  return total
}

/** True when two or more main rows are all ONE meal: its items are served together, not chosen between. */
export function mainsServedTogether(rows: ReadonlyArray<Pick<MealClaimRow, 'mealKeys'>>): boolean {
  return rows.length >= 2 && mealsOf(rows).length === 1
}
